<script lang="ts" setup>
import type {RunHistory} from "@/types";
import type {Config} from "datatables.net";
import DataView from "@/components/DataView.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import KeysetPager from "@/components/KeysetPager.vue";
import RunHistoryFilters from "./RunHistoryFilters.vue";
import RunHistoryRow from "./RunHistoryRow.vue";
import {computed, ref} from "vue";
import type { RunStatus, TimeShortcut } from "@/views/builder/composables/useHistoryManagement";
import { useBootstrapBreakpoint } from "@/composables/useBootstrapBreakpoint";

const isCollapsed = ref(false);

// On mobile the panel overlays the run detail, so collapse it after the user
// picks a run to reveal the detail (standard master-detail pattern). Desktop
// keeps both side-by-side. Hooked to the tap, not the value, so the mount-time
// auto-select doesn't hide the list on load.
const isMobile = useBootstrapBreakpoint("md");

function onSelectRun(run: RunHistory) {
  emit("selectRun", run);
  if (isMobile.value) isCollapsed.value = true;
}

const props = withDefaults(
  defineProps<{
    canvasMode?: string;
    loading: boolean;
    filteredRunHistory: RunHistory[];
    selectedRun: any;
    searchQuery: string;
    statusFilter: string;
    sortBy: string;
    /** 'workflow' (default) shows the panel only in builder history mode and filters by status/sort.
     *  'project' shows the panel always (when canvasMode is unset), surfaces a workflow filter,
     *  and emphasises workflowName in each row. */
    scope?: 'workflow' | 'project';
    /** When the workflow dropdown is visible, the current selection (id or 'all'). */
    workflowFilter?: string;
    /** Available workflow options for the dropdown (project scope). */
    workflowOptions?: { value: string; label: string }[];
    /** Whether to show the workflow filter dropdown. Defaults to true for project scope,
     *  false for workflow scope (since builder is already pinned to a single workflow). */
    showWorkflowFilter?: boolean;
    /** Show a per-row project label — only the org-wide Run History page sets this. The per-project
     *  and builder surfaces already know their project, so it stays off (and the row is unchanged). */
    showProject?: boolean;
    /** Resolves a run's runningProjectId to a display name for the project label. A run with no id,
     *  or an id not in the map, renders as "—". */
    projectNameById?: Map<string, string>;
    /** Show the product-filter dropdown — only the org-wide Run History page sets this. */
    showProductFilter?: boolean;
    /** Current product-filter selection (id or 'all'). */
    productFilter?: string;
    /** Available product options for the dropdown (org-wide scope). */
    productOptions?: { value: string; label: string }[];
    /** The panel's sub-header text. Defaults to "Run History"; the org-wide page passes a scope-aware
     *  title ("All Runs" / "<Product> Runs") since it already has a page-level "Run History" heading. */
    title?: string;
    /** Page size — how many rows the server returns per page. */
    pageLength?: number;
    /** Unpaged total of the matching set (drives "X runs" and the pager). */
    totalCount?: number;
    /** Current page, 0-based. */
    page?: number;
    /** True during a background refetch (e.g. paging) — shows a subtle progress hint. */
    fetching?: boolean;
    // --- Extended filter state (shared between both scopes) ---
    statusFilters?: RunStatus[];
    timeShortcut?: TimeShortcut;
    dateFrom?: Date | null;
    dateTo?: Date | null;
    advancedOpen?: boolean;
    /** True when any advanced filter differs from its default — drives the dot
     *  indicator on the gear button. */
    advancedFiltersDirty?: boolean;
    /** Grouped view: nest child runs under their parent (gates the row expander). */
    grouped?: boolean;
  }>(),
  {
    canvasMode: 'history',
    scope: 'workflow',
    workflowFilter: 'all',
    workflowOptions: () => [],
    showWorkflowFilter: undefined,
    showProject: false,
    projectNameById: undefined,
    showProductFilter: false,
    productFilter: 'all',
    productOptions: () => [],
    title: 'Run History',
    pageLength: 10,
    totalCount: 0,
    page: 0,
    fetching: false,
    statusFilters: () => [],
    timeShortcut: '1d',
    dateFrom: null,
    dateTo: null,
    advancedOpen: false,
    advancedFiltersDirty: false,
    grouped: true,
  }
);

// Default workflow-filter visibility based on scope: project shows it, workflow hides it,
// unless the parent passes an explicit value.
const workflowFilterVisible = computed(() =>
  props.showWorkflowFilter !== undefined ? props.showWorkflowFilter : props.scope === 'project'
);

const emit = defineEmits<{
  updateSearchQuery: [value: string];
  updateStatusFilter: [value: string];
  updateWorkflowFilter: [value: string];
  updateProductFilter: [value: string];
  updateSortBy: [value: string];
  selectRun: [run: RunHistory];
  // Project-scope advanced filter events
  toggleStatus: [status: RunStatus];
  updateTimeShortcut: [shortcut: TimeShortcut];
  updateDateRange: [from: Date | null, to: Date | null];
  updatePageSize: [size: number];
  updatePage: [page: number];
  updateAdvancedOpen: [open: boolean];
  updateGrouped: [value: boolean];
  reset: [];
}>();

// --- Server-side pagination (ADR-007) -------------------------------------
const totalPages = computed(() =>
  Math.max(1, Math.ceil((props.totalCount ?? 0) / (props.pageLength || 1)))
);
const showPager = computed(() => totalPages.value > 1);
const canPrev = computed(() => (props.page ?? 0) > 0);
const canNext = computed(() => (props.page ?? 0) < totalPages.value - 1);

function goPrev() {
  if (canPrev.value) emit("updatePage", (props.page ?? 0) - 1);
}
function goNext() {
  if (canNext.value) emit("updatePage", (props.page ?? 0) + 1);
}

const tableOptions = computed(() => ({
  columns: [
    { data: 'status', title: 'Status' },
    { data: 'workflowName', title: 'Workflow' },
    { data: 'startedAt', title: 'Started' },
    // defaultContent because the property is absent on a run that is still going, not null — the API
    // omits nulls, and DataTables treats a missing one as a column definition error rather than a blank.
    { data: 'completedAt', title: 'Completed', visible: false, defaultContent: '' },
    { data: 'durationInMilliSeconds', title: 'Duration', visible: false },
    { data: 'id', title: 'ID', visible: false }
  ],
  // Null both top cells so DataTables emits NO top layout row at all (a null position is
  // skipped entirely). searching:false below drops the built-in global search box (its own
  // client-side filtering would fight our server-side filters) — without it, nulling the
  // top cells would push that search box elsewhere as a stray "Search:" input.
  layout: {
    topStart: null,
    topEnd: null,
    bottomStart: ''
  },
  // Paging and ordering are both server-side now (ADR-007): the parent fetches one already-sorted
  // page and we render it as-is. Letting DataTables page or sort would fight the server — it would
  // re-paginate a single server page and re-sort it, making the pager and Sort dropdown look broken.
  // searching:false drops the built-in filter box — RunHistoryFilters owns search above.
  paging: false,
  ordering: false,
  searching: false
} as Config));

const data = computed(() => props.filteredRunHistory);

// Show the panel chrome iff:
//   - workflow scope and the builder is in history mode, OR
//   - project scope (always visible, since the parent owns visibility).
const visible = computed(() =>
  props.scope === 'project' || props.canvasMode === 'history'
);
</script>


<template>
  <template v-if="visible">
  <div
      class="history-panel d-flex flex-column"
      :class="{ collapsed: isCollapsed }"
  >
    <div
        class="history-content flex-grow-1 d-flex flex-column"
    >
      <!-- Header (title + run count + advanced toggle) -->
      <div class="p-3 pb-2 flex-shrink-0">
        <div class="d-flex justify-content-between align-items-center">
          <h2 class="h3 mb-0">{{ title }}</h2>
          <div class="d-flex align-items-center gap-2">
            <small class="text-muted">
              {{ totalCount }} run{{ totalCount !== 1 ? "s" : "" }}
            </small>
            <button
              type="button"
              class="btn btn-sm btn-link p-0 advanced-toggle position-relative"
              :class="{ 'is-active': advancedOpen }"
              :aria-pressed="advancedOpen"
              :aria-label="advancedOpen ? 'Hide advanced filters' : 'Show advanced filters'"
              :title="advancedFiltersDirty ? 'Advanced filters active' : 'Advanced filters'"
              @click="emit('updateAdvancedOpen', !advancedOpen)"
            >
              <i class="bi bi-gear"></i>
              <span
                v-if="advancedFiltersDirty"
                class="advanced-dirty-dot"
                aria-hidden="true"
              ></span>
            </button>
          </div>
        </div>
      </div>

      <RunHistoryFilters
        :search-query="searchQuery"
        :status-filters="statusFilters"
        :time-shortcut="timeShortcut"
        :date-from="dateFrom"
        :date-to="dateTo"
        :workflow-filter="workflowFilter"
        :workflow-options="workflowOptions"
        :show-workflow-filter="workflowFilterVisible"
        :show-product-filter="showProductFilter"
        :product-filter="productFilter"
        :product-options="productOptions"
        :sort-by="sortBy"
        :page-size="pageLength"
        :advanced-open="advancedOpen"
        :grouped="grouped"
        @update-search-query="(v) => emit('updateSearchQuery', v)"
        @toggle-status="(s) => emit('toggleStatus', s)"
        @update-time-shortcut="(s) => emit('updateTimeShortcut', s)"
        @update-date-range="(from, to) => emit('updateDateRange', from, to)"
        @update-workflow-filter="(v) => emit('updateWorkflowFilter', v)"
        @update-product-filter="(v) => emit('updateProductFilter', v)"
        @update-sort-by="(v) => emit('updateSortBy', v)"
        @update-page-size="(n) => emit('updatePageSize', n)"
        @update-advanced-open="(o) => emit('updateAdvancedOpen', o)"
        @update-grouped="(v) => emit('updateGrouped', v)"
        @reset="emit('reset')"
      />
      <div class="history-list flex-grow-1">

        <div v-if="loading" class="p-3">
          <SkeletonLoader type="panel" :lines="6" />
        </div>

        <DataView
          v-else
            :data="data"
            :view-mode="'custom'"
            :responsive="false"
            :options="tableOptions"
            @row-click="onSelectRun"
        >
          <template #custom-grid="{ rows }">
            <div class="list-group history-item">
              <RunHistoryRow
                v-for="run in rows"
                :key="run.id"
                :run="run"
                :selected-run-id="selectedRun?.id"
                :show-project="showProject"
                :project-name-by-id="projectNameById"
                :grouped="grouped"
                @select="onSelectRun"
              />
            </div>
          </template>
        </DataView>

      </div>

      <!-- Pager — only when the result set spans more than one page. Server-side offset paging
           (ADR-007); kept outside the scrollable list so it's always reachable. -->
      <KeysetPager
        v-if="showPager"
        class="history-pager flex-shrink-0"
        :label="`Page ${(page ?? 0) + 1} of ${totalPages}`"
        :can-prev="canPrev"
        :can-next="canNext"
        :fetching="fetching"
        @prev="goPrev"
        @next="goNext"
      />
    </div>
  </div>
  <button
      class="history-collapse-toggle bb-btn-secondary-cta btn-sm d-flex align-items-center justify-content-center"
      :class="{ collapsed: isCollapsed }"
      @click="isCollapsed = !isCollapsed"
      :title="isCollapsed ? 'Show run history' : 'Hide run history'"
      :aria-label="isCollapsed ? 'Show run history' : 'Hide run history'"
  >
    <i class="bi bi-chevron-left" :class="{ rotated: isCollapsed }"></i>
  </button>
  </template>
</template>

<style scoped lang="scss">
@import "bootstrap/scss/functions";
@import "bootstrap/scss/variables";

.history-panel {
  width: 300px;
  background: var(--bs-body-bg);
  border-right: 1px solid var(--bs-border-color);
  min-height: 0;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  transition: width 0.3s ease;
}

.history-panel.collapsed {
  width: 0;
  border-right: 0;
}

.history-content {
  min-width: 300px;
  min-height: 0;
}

.history-list {
  flex: 1 1 0;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  scrollbar-width: thin;
}

/* DataTables' bs5 integration wraps every layout region — including the table itself —
   in a Bootstrap `.row mt-2` (see datatables.net-bs5). With no top row emitted (both top
   cells null), the first `.row` is the hidden table's own wrapper (`dt-layout-table`),
   which still contributes its mt-2 as a band above the first run row. The table is already
   display:none in custom mode, so hiding its row just drops that leftover margin; the run
   cards live in a later bottom row and keep their mt-2, leaving a small intentional gap. */
.history-list :deep(.dt-container > .row:first-child) {
  display: none;
}

.history-list::-webkit-scrollbar {
  width: 8px;
}

.history-list::-webkit-scrollbar-track {
  background: var(--bs-tertiary-bg);
  border-radius: 4px;
}

.history-list::-webkit-scrollbar-thumb {
  background-color: var(--bs-secondary-bg);
  border-radius: 4px;
}

.history-list::-webkit-scrollbar-thumb:hover {
  background-color: var(--bs-secondary-color);
}


.history-item {
  --bs-list-group-action-hover-bg: #{$table-hover-bg};
  // Match the main app menu's active highlight (NavBarItem uses --bs-secondary-bg) — a solid grey
  // rather than the table default's semi-transparent overlay, so selection reads consistently.
  --bs-list-group-active-bg: var(--bs-secondary-bg);
  --bs-list-group-active-color: var(--bs-body-color);
  --bs-list-group-active-border-color: var(--bs-border-color);
}



/* History panel toggle button - positioned absolute to avoid taking flex space */
.history-collapse-toggle {
  position: absolute;
  top: 50%;
  left: 300px;
  transform: translateY(-50%);
  width: 24px;
  height: 60px;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-left: none;
  z-index: 2;
  cursor: pointer;
  color: var(--bs-secondary);
  transition: all 0.3s ease;
  // Lift the tab off the canvas so it doesn't blend into the grey/orange
  // hierarchy lines behind it (most noticeable when collapsed on mobile).
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
}

.history-collapse-toggle.collapsed {
  left: 0;
}

.history-collapse-toggle:hover {
  background: var(--bs-secondary-bg);
  color: var(--bs-primary);
}

.history-collapse-toggle i {
  transition: transform 0.3s ease;
}

.history-collapse-toggle i.rotated {
  transform: rotate(180deg);
}


.cursor-pointer {
  cursor: pointer;
}

.advanced-toggle {
  line-height: 1;
  // Default: brand primary blue. Hover / active: BlackBox accent (orange).
  color: var(--bs-primary) !important;

  &:hover,
  &.is-active {
    color: var(--bb-orange, var(--bs-secondary)) !important;
  }
}

.advanced-dirty-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--bb-orange, var(--bs-secondary));
  border: 1.5px solid var(--bs-body-bg);
  pointer-events: none;
}

@media (max-width: 768px) {
  .history-panel {
    width: 100%;
    max-width: 320px;
    position: absolute;
    left: 0;
    top: 0;
    // Span the full height of the positioned ancestor so the inner
    // flex column has room — otherwise the run list (flex: 1) collapses
    // to 0 and only the filter box (flex-shrink: 0) is visible.
    bottom: 0;
    // Overlay everything on the canvas — including the floating canvas/zoom controls
    // (.run-canvas-controls is z-index 4, the builder's .builder-toolbar is z-index 5), so the
    // full-screen filter/list panel sits on top of them rather than letting them poke through.
    z-index: 6;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  }

  .history-collapse-toggle {
    // Keep the toggle clickable above the overlaid panel.
    z-index: 7;
  }

  .history-panel.collapsed {
    width: 0;
  }
}
</style>
